import { useRef, useState, useCallback, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams, Navigate, Link } from 'react-router-dom';
import { LEGAL_DOCUMENTS_BY_SLUG, LEGAL_VERSION, type LegalSlug } from '@/data/legalDocuments';
import { PolicyMarkdown } from './components/PolicyMarkdown';
import { useHideHtmlPreloader } from '@/components/loading';

export function PolicyPage() {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  // This route lives outside MainLayout/SpiritLoader, so nothing else hides the
  // static HTML preloader when it's the first page loaded (direct/deep link).
  useHideHtmlPreloader(true);

  const doc = slug ? LEGAL_DOCUMENTS_BY_SLUG[slug as LegalSlug] : undefined;

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const maxScroll = scrollHeight - clientHeight;
    setScrollProgress(maxScroll > 0 ? scrollTop / maxScroll : 1);
  }, []);

  useEffect(() => {
    setScrollProgress(0);
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [slug]);

  const handleNavigate = useCallback(
    (targetSlug: LegalSlug) => {
      navigate(`/legal/${targetSlug}`);
    },
    [navigate]
  );

  // Always resolve "back" to the hub rather than raw browser history (navigate(-1)):
  // this doc can be reached via a direct/deep link with no prior in-app history,
  // and the hub is always the correct, predictable parent destination.
  const handleBack = useCallback(() => navigate('/legal'), [navigate]);

  if (!doc) {
    return <Navigate to="/legal" replace />;
  }

  return (
    <div className="h-screen bg-gradient-to-b from-[#63345e] to-[#8e4585] flex flex-col">
      {/* Header */}
      <div className="shrink-0">
        {/* Progress bar */}
        <div className="h-1 bg-white/10">
          <div
            className="h-full bg-white/60 transition-[width] duration-150"
            style={{ width: `${scrollProgress * 100}%` }}
          />
        </div>

        <div className="flex items-center gap-3 px-4 py-4">
          <button
            onClick={handleBack}
            className="p-2 -ml-2 text-white/70 hover:text-white hover:bg-white/10 rounded-full transition-colors"
            aria-label="Back to all legal documents"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-lg font-bold text-white font-['Merriweather'] truncate">
            {doc.title}
          </h1>
        </div>
      </div>

      {/* Content */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 bg-white rounded-t-3xl overflow-y-auto overscroll-contain"
      >
        <div className="max-w-2xl mx-auto px-6 py-8 pb-[calc(2rem+env(safe-area-inset-bottom))]">
          {/* Document header */}
          <div className="mb-8 pb-6 border-b border-slate-200">
            <h2 className="text-xl font-bold text-slate-800 font-['Merriweather'] leading-snug mb-1">
              {doc.title}
            </h2>
            <p className="text-sm text-slate-500">{doc.shortDescription}</p>
          </div>

          {/* Markdown body */}
          <PolicyMarkdown content={doc.content} onNavigate={handleNavigate} />

          {/* Footer link back to the hub */}
          <div className="mt-10 pt-6 border-t border-slate-200 text-center">
            <Link
              to="/legal"
              className="text-sm text-[#8e4585] hover:text-[#753a6f] font-medium transition-colors"
            >
              View all legal documents
            </Link>
            <p className="text-[10px] text-slate-300 mt-3">Policy version {LEGAL_VERSION}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
